import React, { useCallback, memo } from 'react'
import styled from 'styled-components/native'
import { useNavigation } from '@react-navigation/native'
import { StyleProp, ViewStyle } from 'react-native'
import { useSelector } from 'APP/Hooks/redux'

import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import useScreenView from 'APP/Hooks/useScreenView'
import { Images } from 'APP/Themes'
import sprigClient from 'APP/Services/Sprig'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { Edge, SafeAreaView } from 'react-native-safe-area-context'

import DrawerHeader from './drawer-header'
import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'

interface HeaderContainerProps {
  includeIndent?: boolean
}

const HeaderContainer = styled.View<HeaderContainerProps>`
  display: flex;
  padding-left: ${({ theme, includeIndent }) => (includeIndent ? theme.metrics.baseMargin : 0)}px;
  flex-shrink: 1;
`

export const StyledRowContainer = styled.View`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  justify-content: center;
  height: ${({ theme }) => theme.metrics.button.height}px;
  flex-shrink: 1;
`

export const StyledContainer = styled(SafeAreaView)`
  background-color: ${({ theme }) => theme.colors.background};
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  padding-top: ${({ theme: { metrics } }) => metrics.baseMargin * 2}px;
`

export const StyledRightButtonContainer = styled.View`
  justify-content: center;
`

export interface HeaderProps {
  title?: string
  hideBackButton?: boolean
  backButtonDisabled?: boolean
  onBackButtonPress?: () => void
  rightButtonOnPress?: () => void
  rightButtonIcon?: string
  rightButtonIconVariant?: 'image' | 'svg' | 'svgUrl' | 'material' | 'materialIcons' | 'ionicons'
  rightButtonTitle?: string
  rightButtonAnalytics?: string
  rightButtonDisabled?: boolean
  headerLeft?: () => React.ReactNode
  headerRight?: () => React.ReactNode
  isModal?: boolean
  isDrawer?: boolean
  style?: StyleProp<ViewStyle>
  subHeader?: React.ReactNode
  edges?: Edge[]
}

// ---------- LONG TERM VISION FOR THIS COMPONENT ----------
// Header component should eventually have three states
// 1. Default header that optionally supports a CTA on the left/right
// 2. Modal style header that requires a CTA on the left (modals slide in from the right)
// 3. Drawer style header that requires a CTA on the right (drawers slide in from the bottom)

const Header = ({
  title,
  hideBackButton,
  backButtonDisabled,
  onBackButtonPress,
  rightButtonOnPress,
  rightButtonIcon,
  rightButtonIconVariant,
  rightButtonTitle,
  rightButtonAnalytics,
  rightButtonDisabled,
  // TODO: "headerLeft" and "headerRight" is temporary solution for backward compatible header
  headerLeft,
  headerRight,
  isModal,
  isDrawer,
  style,
  subHeader,
  edges,
}: HeaderProps) => {
  const navigation = useNavigation()
  const shouldRenderRightButton = Boolean(rightButtonIcon && !!rightButtonOnPress)
  const episode = useSelector((state) => state.history?.episode)

  const onButtonPress = useCallback(() => {
    Analytics.trackEvent('button_click', { button_value: 'Back' })

    // TODO: This is temporary for internal testing. Remove this after beta testing.
    // DIA-84160
    if (episode?.id && episode?.title === 'WH Coach') {
      sprigClient.track('BACK_BUTTON_CLICK_WH_COACH_CHAT', {
        episode_id: episode?.id,
      })
    }
    if (onBackButtonPress) onBackButtonPress()
    else navigation.goBack()
  }, [navigation, onBackButtonPress, episode])

  const { isBigScreen } = useScreenView()

  const includeTitleIndent = !headerLeft && hideBackButton

  // Separated to keep logic clean.
  if (isDrawer) {
    return <DrawerHeader title={title || ''} onClose={onButtonPress} />
  }

  return (
    <StyledContainer edges={edges ?? (isModal ? [] : ['top'])} style={style}>
      <StyledRowContainer>
        {headerLeft
          ? headerLeft()
          : !hideBackButton && (
              <Button
                variant="tertiary"
                widthVariant="circle"
                icon={Images.backIcon}
                iconVariant="image"
                disabled={backButtonDisabled}
                onPress={onButtonPress}
                analyticsName="Back"
                testID="BACK_BTN"
                accessibilityLabel={I18n.t('AccessibilityIcons.back')}
              />
            )}
        <HeaderContainer includeIndent={includeTitleIndent}>
          {Boolean(title) && <Typography variant="h1">{title}</Typography>}
          {subHeader}
        </HeaderContainer>
      </StyledRowContainer>
      <StyledRightButtonContainer>
        {headerRight
          ? headerRight()
          : shouldRenderRightButton && (
              <Button
                variant="tertiary"
                widthVariant="content"
                title={isBigScreen ? rightButtonTitle : undefined}
                icon={rightButtonIcon}
                iconVariant={rightButtonIconVariant}
                analyticsName={rightButtonAnalytics}
                disabled={rightButtonDisabled}
                onPress={rightButtonOnPress}
                testID="TOOLBAR_RIGHT_BUTTON"
              />
            )}
      </StyledRightButtonContainer>
    </StyledContainer>
  )
}

export default memo(Header)

// Custom Header for CmsAppTabsScreen
export const HeaderWithoutBottomPadding = memo(styled(Header).attrs(() => ({
  edges: isEmbeddedEnvironment() ? [] : ['top'],
}))`
  padding-bottom: 0;
`)
